import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STOCK_STATUS_LABELS, formatCents, formatQuantityMilli, type Paginated, type StockDetail, type StockMovement } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { BookingPanel, BOOKING_LABELS, type BookingMode } from './BookingPanel';
import { MovementTable } from './MovementsPage';
import { InventoryTabs, LocationSelect, errorText, formatDay, milliText, parseMilli, statusClass } from './shared';
import './inventory.css';

/** Ein Artikel im Lager (KON-166): Bestand je Ort und Charge, Einstellungen der Lagerführung und die letzten Bewegungen */
export function StockDetailPage() {
  const { productId = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const [booking, setBooking] = useState<BookingMode | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const mayWrite = can('inventory.write');
  const detail = useQuery({
    queryKey: ['inventory-detail', productId],
    queryFn: async () => (await api<{ data: StockDetail }>('GET', `/inventory/stock/${productId}`)).data,
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 2,
  });
  const moves = useQuery({
    queryKey: ['inventory-movements', { productId, page: 1 }],
    queryFn: () => api<Paginated<StockMovement>>('GET', `/inventory/movements?productId=${productId}&perPage=10`),
  });
  const d = detail.data;

  if (detail.isError) {
    return (
      <>
        <InventoryTabs />
        <div className="alert alert--error">{errorText(detail.error, 'Der Artikel konnte nicht geladen werden.')}</div>
        <Link to="/lager">Zurück zum Lager</Link>
      </>
    );
  }
  if (!d) return <p className="muted">Wird geladen …</p>;

  return (
    <>
      <div className="page-head">
        <h1>{d.name}</h1>
        <span className={`inv-badge ${statusClass[d.status]}`}>{STOCK_STATUS_LABELS[d.status]}</span>
      </div>
      <InventoryTabs />
      <p className="muted"><Link to="/lager">← Alle Bestände</Link>{d.sku ? ` · Artikelnummer ${d.sku}` : ''} · <Link to={`/produkte/${d.productId}`}>Produkt öffnen</Link></p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <div className="inv-tiles">
        <div className="card inv-tile"><span className="muted small">Bestand</span><strong>{formatQuantityMilli(d.quantityMilli)} {d.unitSymbol ?? ''}</strong></div>
        <div className="card inv-tile"><span className="muted small">Mindestbestand</span><strong>{d.minStockMilli ? `${formatQuantityMilli(d.minStockMilli)} ${d.unitSymbol ?? ''}` : '–'}</strong></div>
        {d.potentialRevenueCents !== null && <div className="card inv-tile"><span className="muted small">Möglicher Umsatz netto</span><strong>{formatCents(d.potentialRevenueCents)}</strong>{d.salePriceCents !== null && <span className="muted small">{formatQuantityMilli(Math.max(d.quantityMilli, 0))} × {formatCents(d.salePriceCents)} Verkaufspreis</span>}</div>}
        {d.valueCents !== null && <div className="card inv-tile"><span className="muted small">Bestandswert (Einkauf)</span><strong>{formatCents(d.valueCents)}</strong>{d.purchasePriceCents !== null && <span className="muted small">zu {formatCents(d.purchasePriceCents)} Einkaufspreis</span>}</div>}
      </div>
      {mayWrite && (
        <div className="inv-actions">
          {(['receipt', 'issue', 'transfer', 'adjustment'] as BookingMode[]).map((m) => (
            <button key={m} type="button" className={`btn ${booking === m ? 'btn--primary' : 'btn--secondary'}`} onClick={() => setBooking(booking === m ? null : m)}>{BOOKING_LABELS[m]}</button>
          ))}
        </div>
      )}
      {booking && <BookingPanel key={booking} mode={booking} productId={productId} onClose={() => setBooking(null)} onDone={(moved) => { setBooking(null); setMessage({ ok: true, text: `Gebucht (${moved.length} ${moved.length === 1 ? 'Bewegung' : 'Bewegungen'}).` }); }} />}

      <section className="card">
        <h2>Bestand nach Lagerort</h2>
        <div className="table-card">
          <table className="table table--cards">
            <thead><tr><th>Lagerort</th>{d.batchTracking && <th>Charge</th>}{d.batchTracking && <th>Haltbar bis</th>}<th className="num">Menge</th></tr></thead>
            <tbody>
              {d.levels.length === 0 && <tr><td colSpan={4} className="empty">Kein Bestand. Buchen Sie einen Wareneingang.</td></tr>}
              {d.levels.map((l, i) => (
                <tr key={`${l.locationId}-${l.batchId ?? i}`}>
                  <td data-label="Lagerort">{l.locationPath}</td>
                  {d.batchTracking && <td data-label="Charge">{l.batchNumber ?? '–'}</td>}
                  {d.batchTracking && <td data-label="Haltbar bis">{formatDay(l.bestBefore)}</td>}
                  <td data-label="Menge" className="num">{formatQuantityMilli(l.quantityMilli)} {d.unitSymbol ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {mayWrite && <SettingsCard detail={d} onSaved={(text) => { client.invalidateQueries({ queryKey: ['inventory-detail'] }); client.invalidateQueries({ queryKey: ['inventory-stock'] }); client.invalidateQueries({ queryKey: ['inventory-stock-options'] }); setMessage({ ok: true, text }); }} onError={(text) => setMessage({ ok: false, text })} />}

      <section className="card">
        <h2>Letzte Bewegungen</h2>
        <MovementTable movements={moves.data?.data ?? []} loading={moves.isLoading} compact />
        <p><Link to={`/lager/bewegungen?productId=${productId}`}>Alle Bewegungen dieses Artikels</Link></p>
      </section>
    </>
  );
}

function SettingsCard({ detail, onSaved, onError }: { detail: StockDetail; onSaved: (text: string) => void; onError: (text: string) => void }) {
  const [min, setMin] = useState(milliText(detail.minStockMilli));
  const [batch, setBatch] = useState(detail.batchTracking);
  const [location, setLocation] = useState(detail.defaultLocationId ?? '');
  const hasMoves = detail.levels.length > 0 || detail.batches.length > 0;
  const save = useMutation({
    mutationFn: () => {
      const m = parseMilli(min);
      if (m === null && min.trim()) throw new Error('Bitte geben Sie den Mindestbestand als Zahl ein.');
      return api('PUT', `/inventory/stock/${detail.productId}/settings`, { tracked: true, minStockMilli: m ?? 0, batchTracking: batch, defaultLocationId: location || null });
    },
    onSuccess: () => onSaved('Die Einstellungen sind gespeichert.'),
    onError: (e) => onError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Das Speichern hat nicht geklappt.')),
  });
  const untrack = useMutation({
    mutationFn: () => api('PUT', `/inventory/stock/${detail.productId}/settings`, { tracked: false }),
    onSuccess: () => { window.location.assign('/lager'); },
    onError: (e) => onError(errorText(e, 'Die Lagerführung konnte nicht ausgeschaltet werden.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className="card" onSubmit={submit}>
      <h2>Lagerführung</h2>
      <div className="form-grid">
        <label className="field">
          <span>Mindestbestand{detail.unitSymbol ? ` (${detail.unitSymbol})` : ''}</span>
          <input className="input" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
          <small className="muted">Darunter erscheint der Artikel in der Warnliste.</small>
        </label>
        <LocationSelect value={location} onChange={setLocation} label="Standardlagerort" allowEmpty emptyLabel="Keiner" />
        <label className="field inv-check">
          <span><input type="checkbox" checked={batch} onChange={(e) => setBatch(e.target.checked)} disabled={hasMoves} /> In Chargen führen</span>
          <small className="muted">{hasMoves ? 'Nach der ersten Buchung nicht mehr änderbar.' : 'Mit Chargennummer und Mindesthaltbarkeit, Entnahme nach frühestem Ablauf.'}</small>
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
        <button type="button" className="btn btn--secondary" disabled={untrack.isPending} onClick={() => window.confirm('Lagerführung für diesen Artikel ausschalten? Das geht nur bei Bestand 0.') && untrack.mutate()}>Aus dem Lager nehmen</button>
      </div>
    </form>
  );
}
